import type { CTRL_SCHEMA } from "@repo/sdk/types";
import { useHttp, type HttpConfiguration } from "@repo/utils/hooks";
import { useCtrlClient } from "../contexts/CtrlApiProvider";

type BootstrapUpdatesResponse =
  CTRL_SCHEMA["OpenapigenBootstrapUpdatesResponse"];

/**
 * Hook for fetching bootstrap updates for a tenant using SWR
 *
 * @param tenantId - The tenant ID to fetch bootstrap updates for
 * @param config - Optional SWR configuration
 * @returns SWR response with updates data, error, loading states, and mutation function
 *
 * @example
 * ```tsx
 * function TenantBootstrapUpdates({ tenantId }: { tenantId: string }) {
 *   const { data: updates, error, isLoading } = useTenantBootstrapUpdates(tenantId)
 *
 *   if (isLoading) return <div>Loading...</div>
 *   if (error) return <div>Error: {error.message}</div>
 *
 *   return (
 *     <div>
 *       <h3>Bootstrap Updates</h3>
 *       <pre>{JSON.stringify(updates, null, 2)}</pre>
 *     </div>
 *   )
 * }
 * ```
 */
export function useTenantBootstrapUpdates(
  tenantId: string | null | undefined,
  config?: HttpConfiguration
) {
  const client = useCtrlClient();

  return useHttp<BootstrapUpdatesResponse>(
    tenantId ? `tenants/${tenantId}/bootstrap/updates` : null,
    async () => {
      if (!tenantId) throw new Error("Tenant ID is required");
      return await client.getTenantBootstrapUpdates(tenantId);
    },
    config
  );
}
